import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CustomSiteSnippet } from '../types/custome-snippet.type';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class SnippetInjectorService {

    private document = inject(DOCUMENT);
    private platformId = inject(PLATFORM_ID);

    inject(snippets: CustomSiteSnippet[]) {
        snippets
            .filter((s) => s.status)
            .forEach((snippet) => this.injectSnippet(snippet));
    }

    /**
     * Add snippets to the html
     * @param snippet
     */
    private injectSnippet(snippet: CustomSiteSnippet) {
        if (isPlatformServer(this.platformId)) {
            this.injectOnServer(snippet);
        } else if (isPlatformBrowser(this.platformId)) {
            this.injectOnClient(snippet);
        }
    }

    /**
     * Inject on the server side
     * @param snippet
     */
    private injectOnServer(snippet: CustomSiteSnippet) {
        const container = this.document.createElement('div');
        container.innerHTML = snippet.snippet;

        const allowedTags = ['NOSCRIPT', 'LINK', 'STYLE', 'META'];

        Array.from(container.childNodes).forEach((node) => {
            if (
                node.nodeType === Node.ELEMENT_NODE &&
                allowedTags.includes((node as Element).tagName)
            ) {
                const position = this.resolvePosition(node as Element, snippet.position);
                this.insertNode(node as HTMLElement, position);
            }
        });
    }

    /**
     * <link rel="stylesheet"> in <body> triggers a re-layout on load, and
     * <meta> in <body> is typically ignored by SEO crawlers, so both are
     * forced into <head> even when the snippet's saved position is
     * body-start/body-end. Other tags pass through unchanged.
     */
    private resolvePosition(node: Element, requestedPosition: string): string {
        if (requestedPosition === 'head') {
            return requestedPosition;
        }

        const isStylesheetLink =
            node.tagName === 'LINK' &&
            (node.getAttribute('rel') || '').trim().toLowerCase() === 'stylesheet';
        const isMeta = node.tagName === 'META';

        if (isStylesheetLink || isMeta) {
            const reason = isMeta
                ? '<meta> in <body> is ignored by many crawlers'
                : '<link rel="stylesheet"> in <body> triggers a re-layout';
            console.warn(
                `[SnippetInjector] <${node.tagName.toLowerCase()}> forced to <head> (requested position: ${requestedPosition}) — ${reason}.`
            );
            return 'head';
        }

        return requestedPosition;
    }

    /**
     * Inject snippet on client
     * @param snippet
     */
    private injectOnClient(snippet: CustomSiteSnippet) {
        const injectWhenIdle = () => {
            const inject = () => this.processSnippet(snippet);

            if ('requestIdleCallback' in window) {
                (window as any).requestIdleCallback(inject);
            } else {
                setTimeout(inject, 0);
            }
        };

        if (this.document.readyState === 'complete') {
            injectWhenIdle();
        } else {
            window.addEventListener('load', injectWhenIdle, { once: true });
        }
    }

    /**
     *  To process the snippet
     * @param snippet
     */
    private processSnippet(snippet: CustomSiteSnippet) {
        const container = this.document.createElement('div');
        container.innerHTML = snippet.snippet;

        const allowedClientTags = ['SCRIPT', 'IFRAME'];

        Array.from(container.childNodes).forEach((node) => {
            if (
                node.nodeType === Node.ELEMENT_NODE &&
                allowedClientTags.includes((node as Element).tagName)
            ) {
                if ((node as Element).tagName === 'SCRIPT') {
                    const newScript = this.document.createElement('script');
                    const oldScript = node as HTMLScriptElement;

                    Array.from(oldScript.attributes).forEach((attr) => {
                        newScript.setAttribute(attr.name, attr.value);
                    });

                    if (oldScript.textContent) {
                        newScript.textContent = oldScript.textContent;
                    }

                    this.insertNode(newScript, snippet.position);
                } else {
                    this.insertNode(node as HTMLElement, snippet.position);
                }
            }
        });
    }


    /**
     * Insert node at the configured position
     */
    private insertNode(node: HTMLElement, position: string) {
        switch (position) {
            case 'head':
                this.document.head.appendChild(node);
                break;

            case 'body-start': {
                const body = this.document.body;
                const firstChild = body.firstChild;

                if (firstChild) {
                    body.insertBefore(node, firstChild);
                } else {
                    body.appendChild(node);
                }
                break;
            }

            case 'body-end':
            default:
                this.document.body.appendChild(node);
                break;
        }
    }

    /**
     * To Inject PPC snippets
     */
    injectPPCSnippets() {
        if (!environment.ppc.gtm_id) return;
        const PPCSnippets: CustomSiteSnippet[] = [
            {
                position: 'head',
                snippet: `
                        <!-- Google Tag Manager -->
                            <script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                            })(window,document,'script','dataLayer','${environment.ppc.gtm_id}');</script>
                        <!-- End Google Tag Manager -->
                        `.replace(/\s+/g, ' ').trim(),
                status: true,
            },
            {
                position: 'body-start',
                snippet: `
                        <!-- Google Tag Manager (noscript) -->
                            <noscript><iframe src="https://www.googletagmanager.com/gtm.js?id=${environment.ppc.gtm_id}"
                            height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>
                        <!-- End Google Tag Manager (noscript) -->
                        `.replace(/\s+/g, ' ').trim(),
                status: true,
            },
        ];
        this.inject(PPCSnippets);
    }
}
